import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as languageService from "./service";
import useTableSearch from "../../../hooks/useTableSearch";
import Dialog from "../../dialog/Dialog";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import {
  AdminPageLayout,
  AdminPageHeader,
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  AdminTableFooter,
  AdminTableToolbar,
  TableSearchInput,
  TableEmptyRow,
  EditActionButton,
  DeleteActionButton,
  TableActionsCell,
  StatusBadge,
  getStatusVariant,
} from "../../common/adminTable";

const Language = () => {
  const queryClient = useQueryClient();

  // State management
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const { searchQuery, setSearchQuery, activeSearch } = useTableSearch();
  const [languageTitle, setLanguageTitle] = useState("");
  const [languageStatus, setLanguageStatus] = useState(1);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [titleError, setTitleError] = useState("");

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  // Queries
  const { data: languagesResponse, isLoading: isLoadingLanguages } = useQuery({
    queryKey: ["languages", currentPage, activeSearch],
    queryFn: () =>
      languageService.list({ page: currentPage, search: activeSearch || undefined }),
    keepPreviousData: true,
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: languageService.create,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.message || "Language added successfully!");
      setAddDialogOpen(false);
      setLanguageTitle("");
      queryClient.invalidateQueries(["languages"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(error.response?.data?.message || "Failed to add language");
    },
  });

  const updateMutation = useMutation({
    mutationFn: languageService.update,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.message || "Language updated successfully!");
      setEditDialogOpen(false);
      setLanguageTitle("");
      setLanguageStatus(1);
      setSelectedLanguage(null);
      queryClient.invalidateQueries(["languages"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to update language"
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: languageService.deleteLanguage,
    onSuccess: (success) => {
      setIsLoading(false);
      showSuccessMessage(success.message || "Language deleted successfully!");
      setDeleteDialogOpen(false);
      setSelectedLanguage(null);
      queryClient.invalidateQueries(["languages"]);
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to delete language"
      );
    },
  });

  const getByIdMutation = useMutation({
    mutationFn: languageService.getById,
    onSuccess: (response) => {
      if (response.data) {
        setSelectedLanguage(response.data);
        setLanguageTitle(response.data.name);
        setLanguageStatus(response.data.status);
        setEditDialogOpen(true);
        setIsLoading(false);
      } else {
        setIsLoading(false);
        showErrorMessage("Language not found");
      }
    },
    onError: (error) => {
      setIsLoading(false);
      showErrorMessage(
        error.response?.data?.message || 
        error.message || 
        "Failed to fetch language details"
      );
    },
  });

  // Helper functions
  const showSuccessMessage = (message) => {
    setSuccessMessage(message);
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
      setSuccessMessage("");
    }, 3000);
  };

  const showErrorMessage = (errorMsg) => {
    setErrorMessage(errorMsg);
    setShowError(true);
    setTimeout(() => {
      setShowError(false);
      setErrorMessage("");
    }, 3000);
  };

  // Action handlers
  const handleAddClick = () => {
    setLanguageTitle("");
    setLanguageStatus(1);
    setAddDialogOpen(true);
  };

  const handleEditClick = (languageId) => {
    setIsLoading(true);
    getByIdMutation.mutate(languageId);
  };

  const handleSaveLanguage = () => {
    setIsLoading(true);
    if (!languageTitle.trim()) {
      setIsLoading(false);
      setTitleError("Language title is required");
      return;
    }
   
    createMutation.mutate({
      name: languageTitle,
      status: Number(languageStatus),
    });
  };

  const handleUpdateLanguage = () => {
    if (!languageTitle.trim()) {
      setTitleError("Language title is required");
      return;
    }

    setIsLoading(true);
    if (selectedLanguage) {
      updateMutation.mutate({
        id: selectedLanguage.id,
        name: languageTitle,
        status: Number(languageStatus)
      });
    }
  };

  const handleDeleteClick = (language) => {
    setSelectedLanguage(language);
    setDeleteDialogOpen(true);
  };

  const handleDeleteLanguage = () => {
    setIsLoading(true);
    if (selectedLanguage) {
      deleteMutation.mutate(selectedLanguage.id);
    }
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) {
      setCurrentPage(page);
    }
  };

  const languages = languagesResponse?.data || [];
  
  const pagination = {
    currentPage: languagesResponse?.current_page || 1,
    lastPage: languagesResponse?.last_page || 1,
    perPage: languagesResponse?.per_page || 10,
    total: languagesResponse?.total || 0,
    from: languagesResponse?.from || 0,
    to: languagesResponse?.to || 0,
  };

  return (
    <AdminPageLayout>
      {/* Error Toster */}
      {showError && (
        <Toster
          message={errorMessage}
          type="error"
          duration={3000}
          onClose={() => setShowError(false)}
        />
      )}

      {/* Success Toster */}
      {showSuccess && successMessage && (
        <Toster
          message={successMessage}
          type="success"
          duration={3000}
          onClose={() => {
            setShowSuccess(false);
            setSuccessMessage("");
          }}
        />
      )}

      <AdminPageHeader
        title={ADMIN_PAGE_META.language.title}
        subtitle={ADMIN_PAGE_META.language.subtitle}
      />

      <AdminPageCard>
        <AdminPageCardToolbar>
          <AdminTableToolbar className="mb-0">
            <TableSearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search languages..."
            />
            <button
              onClick={handleAddClick}
              className="bg-primary text-white px-4 py-1 rounded-lg border transition-all"
            >
              Add Language
            </button>
          </AdminTableToolbar>
        </AdminPageCardToolbar>

        <AdminTable
          className="shadow-none rounded-none"
          footer={
            !isLoadingLanguages && languages && languages.length > 0 ? (
              <AdminTableFooter>
                <Pagination
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  showInfo={true}
                />
              </AdminTableFooter>
            ) : null
          }
        >
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Language Name</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {isLoadingLanguages ? (
            <tr>
              <td colSpan={4} className="px-4 py-8 text-center">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  <span className="ml-2">Loading...</span>
                </div>
              </td>
            </tr>
          ) : languages && languages.length > 0 ? (
            languages.map((language, index) => (
              <AdminTableRow key={language.id}>
                <AdminTableCell>{index + 1}</AdminTableCell>
                <AdminTableCell>{language.name}</AdminTableCell>
                <AdminTableCell>
                  <StatusBadge variant={getStatusVariant(language.status)}>
                    {language.status === 1 ? "Active" : "In-Active"}
                  </StatusBadge>
                </AdminTableCell>
                <TableActionsCell>
                  <EditActionButton
                    showLabel={false}
                    onClick={() => handleEditClick(language.id)}
                  />
                  <DeleteActionButton
                    showLabel={false}
                    onClick={() => handleDeleteClick(language)}
                  />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow colSpan={4} emptyMessage="No languages found" />
          )}
        </AdminTableBody>
      </AdminTable>
      </AdminPageCard>

      {/* Add Language Dialog */}
      <Dialog
        open={addDialogOpen}
        isLoading={isLoading}
        onClose={() => {
          setAddDialogOpen(false);
          setTitleError("");
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-bs-add"></i>
            Create Language
          </div>
        }
        primaryButtonText="Add Language"
        primaryButtonAction={handleSaveLanguage}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setAddDialogOpen(false);
          setTitleError("");
        }}
        width="w-[550px]"
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-4 grid-cols-2 gap-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language Title
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                titleError ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter language title"
              value={languageTitle}
              onChange={(e) => {
                setLanguageTitle(e.target.value);
                if (e.target.value.trim()) {
                  setTitleError("");
                }
              }}
            />
            {titleError && (
              <p className="text-red-500 text-xs mt-1">{titleError}</p>
            )}
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={languageStatus}
              onChange={(e) => setLanguageStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        isLoading={isLoading}
        onClose={() => {
          setEditDialogOpen(false);
          setTitleError("");
        }}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-pencil text-[16px]"></i>
            Edit Language
          </div>
        }
        primaryButtonText="Update"
        primaryButtonAction={handleUpdateLanguage}
        closeButtonText="Cancel"
        closeButtonAction={() => {
          setEditDialogOpen(false);
          setTitleError("");
        }}
        width="w-[500px]"
      >
        <div className="grid grid-cols-2 gap-2">
          <div className="mb-4 grid-cols-2 gap-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Language Title
            </label>
            <input
              type="text"
              className={`text-sm w-full px-3 py-1 border ${
                titleError ? "border-red-500" : "border-gray-300"
              } rounded-lg focus:outline-none focus:border-primary focus:ring-none`}
              placeholder="Enter language title"
              value={languageTitle}
              onChange={(e) => {
                setLanguageTitle(e.target.value);
                if (e.target.value.trim()) {
                  setTitleError("");
                }
              }}
            />
            {titleError && (
              <p className="text-red-500 text-xs mt-1">{titleError}</p>
            )}
          </div>
          <div className="mb-4 grid-cols-2 gap-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="text-sm w-full px-3 py-1 border border-gray-300 rounded-lg focus:outline-none focus:border-primary focus:ring-none"
              value={languageStatus}
              onChange={(e) => setLanguageStatus(Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>In-Active</option>
            </select>
          </div>
        </div>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={
          <div className="flex items-center gap-2">
            <i className="fi fi-rr-trash text-[16px]"></i>
            Delete Language
          </div>
        }
        primaryButtonText="Delete"
        primaryButtonAction={handleDeleteLanguage}
        closeButtonText="Cancel"
        closeButtonAction={() => setDeleteDialogOpen(false)}
        isLoading={isLoading}
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Are you sure you want to delete this language? This action cannot be
            undone.
          </p>
        </div>
      </Dialog>
    </AdminPageLayout>
  );
};

export default Language;